import React, { useState } from 'react';
import { Button } from './ui/button';
import { Card, CardContent } from './ui/card';
import { Input } from './ui/input';
import { Textarea } from './ui/textarea';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from './ui/select';
import { 
  Phone, Mail, MapPin, Clock, 
  Wrench, Settings, Monitor, Server, HardDrive,
  Snowflake, Thermometer, Megaphone, FileText, 
  Building, Truck, Award, Users, Shield, SprayCan
} from 'lucide-react';
import { companyInfo, services, benefits, sectors, heroImage } from '../data/mock';
import axios from 'axios';

const iconMap = {
  Wrench, Settings, Monitor, Server, HardDrive,
  Snowflake, Thermometer, Megaphone, FileText,
  Building, Truck, Award, Users, Shield, SprayCan
};

const Landing = () => {
  const [formData, setFormData] = useState({
    nombre: '',
    empresa: '',
    email: '',
    telefono: '',
    servicio: '',
    mensaje: ''
  });
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [submitMessage, setSubmitMessage] = useState('');

  const handleInputChange = (field, value) => {
    setFormData(prev => ({ ...prev, [field]: value }));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setIsSubmitting(true);
    
    try {
      const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
      const response = await axios.post(`${BACKEND_URL}/api/quote`, formData);
      
      if (response.data) {
        setSubmitMessage('¡Cotización enviada exitosamente! Nos pondremos en contacto contigo pronto.');
        setFormData({
          nombre: '',
          empresa: '',
          email: '',
          telefono: '',
          servicio: '',
          mensaje: ''
        });
      }
    } catch (error) {
      console.error('Error al enviar cotización:', error);
      setSubmitMessage('Error al enviar la cotización. Por favor intenta nuevamente.');
    } finally {
      setIsSubmitting(false);
      setTimeout(() => setSubmitMessage(''), 5000);
    }
  };

  const scrollToSection = (sectionId) => {
    document.getElementById(sectionId)?.scrollIntoView({ behavior: 'smooth' });
  };

  return (
    <div className="min-h-screen bg-white">
      {/* Header */}
      <header className="bg-white shadow-sm border-b border-gray-100 sticky top-0 z-50">
        <div className="container mx-auto px-4 py-4">
          <div className="flex items-center justify-between">
            <div className="flex items-center space-x-4">
              <img 
                src="https://customer-assets.emergentagent.com/job_integral-supply/artifacts/tl2ojow0_WhatsApp%20Image%202025-10-06%20at%2016.54.25.jpeg" 
                alt="GRUPO PINACSA Logo" 
                className="h-16 w-auto object-contain"
              />
            </div>
            
            <nav className="hidden md:flex items-center space-x-8">
              <button onClick={() => scrollToSection('inicio')} className="text-slate-700 hover:text-amber-600 transition-colors duration-300">Inicio</button>
              <button onClick={() => scrollToSection('servicios')} className="text-slate-700 hover:text-amber-600 transition-colors duration-300">Servicios</button>
              <button onClick={() => scrollToSection('nosotros')} className="text-slate-700 hover:text-amber-600 transition-colors duration-300">Nosotros</button>
              <button onClick={() => scrollToSection('clientes')} className="text-slate-700 hover:text-amber-600 transition-colors duration-300">Clientes</button>
              <button onClick={() => scrollToSection('contacto')} className="text-slate-700 hover:text-amber-600 transition-colors duration-300">Contacto</button>
              <div className="flex items-center space-x-3">
                <a
                  href="https://wa.me/message/ITBOM5CJGNGQO1"
                  target="_blank"
                  rel="noopener noreferrer"
                  className="bg-green-500 hover:bg-green-600 text-white font-semibold px-4 py-2 rounded-lg transition-all duration-300 shadow-lg hover:shadow-xl transform hover:scale-105 flex items-center space-x-2"
                >
                  <Phone className="w-4 h-4" />
                  <span>WhatsApp</span>
                </a>
                <Button 
                  onClick={() => scrollToSection('cotizacion')}
                  className="bg-gradient-to-r from-amber-500 to-orange-600 hover:from-amber-600 hover:to-orange-700 text-white font-semibold px-6 py-2 rounded-lg transition-all duration-300 shadow-lg hover:shadow-xl transform hover:scale-105"
                >
                  Solicitar Cotización
                </Button>
              </div>
            </nav>
          </div>
        </div>
      </header>

      {/* Hero Section */}
      <section id="inicio" className="relative min-h-screen flex items-center justify-center overflow-hidden">
        <div 
          className="absolute inset-0 bg-cover bg-center bg-no-repeat"
          style={{ backgroundImage: `url(${heroImage})` }}
        >
          <div className="absolute inset-0 bg-gradient-to-r from-slate-900/85 via-slate-800/70 to-transparent"></div>
        </div>
        
        <div className="relative z-10 container mx-auto px-4 text-center md:text-left">
          <div className="max-w-4xl">
            <h1 className="text-5xl md:text-7xl font-bold text-white mb-6 leading-tight">
              {companyInfo.tagline}
            </h1>
            <p className="text-xl md:text-2xl text-gray-200 mb-8 max-w-2xl">
              {companyInfo.subtitle}
            </p>
            <div className="flex flex-col sm:flex-row gap-4 justify-center md:justify-start">
              <Button 
                onClick={() => scrollToSection('servicios')}
                size="lg"
                className="bg-gradient-to-r from-slate-700 to-slate-800 hover:from-slate-800 hover:to-slate-900 text-white font-semibold px-8 py-4 text-lg rounded-lg transition-all duration-300 shadow-lg hover:shadow-xl transform hover:scale-105"
              >
                Explorar servicios
              </Button>
              <Button 
                onClick={() => scrollToSection('cotizacion')}
                size="lg"
                variant="outline"
                className="border-2 border-amber-500 text-amber-500 hover:bg-amber-500 hover:text-white font-semibold px-8 py-4 text-lg rounded-lg transition-all duration-300 shadow-lg hover:shadow-xl transform hover:scale-105"
              >
                Cotizar ahora
              </Button>
            </div>
          </div>
        </div>
      </section>

      {/* Services Section */}
      <section id="servicios" className="py-20 bg-gray-50">
        <div className="container mx-auto px-4">
          <div className="text-center mb-16">
            <h2 className="text-4xl md:text-5xl font-bold text-slate-800 mb-4">Nuestros Servicios</h2>
            <p className="text-xl text-slate-600 max-w-3xl mx-auto">Soluciones integrales para todas las necesidades de tu empresa</p>
          </div>
          
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-6">
            {services.map((service) => {
              const IconComponent = iconMap[service.icon];
              return (
                <Card key={service.id} className="group hover:shadow-xl transition-all duration-300 transform hover:-translate-y-2 border-0 bg-white">
                  <CardContent className="p-6">
                    <div className="relative h-40 mb-4 rounded-lg overflow-hidden">
                      <img 
                        src={service.image} 
                        alt={service.title}
                        className="w-full h-full object-cover group-hover:scale-110 transition-transform duration-300"
                        onError={(e) => {
                          e.target.src = '/api/placeholder/300/200';
                        }}
                      />
                      <div className="absolute inset-0 bg-gradient-to-t from-slate-800/60 to-transparent flex items-end justify-center pb-4">
                        {IconComponent && <IconComponent className="text-white w-8 h-8" />}
                      </div>
                    </div>
                    <h3 className="font-bold text-slate-800 mb-2 group-hover:text-amber-600 transition-colors duration-300">{service.title}</h3>
                    <p className="text-slate-600 text-sm leading-relaxed">{service.description}</p>
                  </CardContent>
                </Card>
              );
            })}
          </div>
        </div>
      </section>

      {/* Benefits Section */}
      <section id="nosotros" className="py-20 bg-slate-800">
        <div className="container mx-auto px-4">
          <div className="text-center mb-16">
            <h2 className="text-4xl md:text-5xl font-bold text-white mb-4">¿Por qué elegirnos?</h2>
            <p className="text-xl text-gray-300 max-w-3xl mx-auto">La confianza de cientos de empresas nos respalda</p>
          </div>
          
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-8">
            {benefits.map((benefit, index) => {
              const IconComponent = iconMap[benefit.icon];
              return (
                <div key={index} className="text-center group">
                  <div className="bg-gradient-to-r from-amber-500 to-orange-600 w-16 h-16 rounded-full flex items-center justify-center mx-auto mb-4 group-hover:scale-110 transition-transform duration-300">
                    {IconComponent && <IconComponent className="text-white w-8 h-8" />}
                  </div>
                  <h3 className="text-xl font-bold text-white mb-2">{benefit.title}</h3>
                  <p className="text-gray-300">{benefit.description}</p>
                </div>
              );
            })}
          </div>
        </div>
      </section>

      {/* Clients Section */}
      <section id="clientes" className="py-20 bg-white">
        <div className="container mx-auto px-4">
          <div className="text-center mb-16">
            <h2 className="text-4xl md:text-5xl font-bold text-slate-800 mb-4">Sectores que Atendemos</h2>
            <p className="text-xl text-slate-600 max-w-3xl mx-auto">Experiencia comprobada en múltiples industrias</p>
          </div>
          
          <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-6 gap-6">
            {sectors.map((sector, index) => (
              <div key={index} className="text-center p-6 bg-gray-50 rounded-lg hover:bg-gradient-to-r hover:from-slate-700 hover:to-slate-800 hover:text-white transition-all duration-300 group">
                <h3 className="font-semibold text-slate-800 group-hover:text-white transition-colors duration-300">{sector}</h3>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Quote Form Section */}
      <section id="cotizacion" className="py-20 bg-gradient-to-r from-slate-50 to-gray-100">
        <div className="container mx-auto px-4">
          <div className="max-w-2xl mx-auto">
            <div className="text-center mb-12">
              <h2 className="text-4xl md:text-5xl font-bold text-slate-800 mb-4">Solicita tu Cotización</h2>
              <p className="text-xl text-slate-600">Recibe tu cotización en menos de 24 horas</p>
            </div>
            
            <Card className="shadow-2xl border-0">
              <CardContent className="p-8">
                {submitMessage && (
                  <div className={`mb-6 p-4 rounded-lg text-center font-semibold ${
                    submitMessage.includes('Error') ? 'bg-red-100 text-red-800' : 'bg-green-100 text-green-800'
                  }`}>
                    {submitMessage}
                  </div>
                )}
                
                <form onSubmit={handleSubmit} className="space-y-6">
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                    <div>
                      <label className="block text-sm font-medium text-slate-700 mb-2">Nombre completo *</label>
                      <Input
                        type="text"
                        required
                        value={formData.nombre}
                        onChange={(e) => handleInputChange('nombre', e.target.value)}
                        className="w-full border-slate-300 focus:border-amber-500 focus:ring-amber-500"
                        placeholder="Tu nombre completo"
                      />
                    </div>
                    <div>
                      <label className="block text-sm font-medium text-slate-700 mb-2">Empresa *</label>
                      <Input
                        type="text"
                        required
                        value={formData.empresa}
                        onChange={(e) => handleInputChange('empresa', e.target.value)}
                        className="w-full border-slate-300 focus:border-amber-500 focus:ring-amber-500"
                        placeholder="Nombre de tu empresa"
                      />
                    </div>
                  </div>
                  
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                    <div>
                      <label className="block text-sm font-medium text-slate-700 mb-2">Correo electrónico *</label>
                      <Input
                        type="email"
                        required
                        value={formData.email}
                        onChange={(e) => handleInputChange('email', e.target.value)}
                        className="w-full border-slate-300 focus:border-amber-500 focus:ring-amber-500"
                        placeholder="tu@empresa.com"
                      />
                    </div>
                    <div>
                      <label className="block text-sm font-medium text-slate-700 mb-2">Teléfono *</label>
                      <Input
                        type="tel"
                        required
                        value={formData.telefono}
                        onChange={(e) => handleInputChange('telefono', e.target.value)}
                        className="w-full border-slate-300 focus:border-amber-500 focus:ring-amber-500"
                        placeholder="Tu número de teléfono"
                      />
                    </div>
                  </div>
                  
                  <div>
                    <label className="block text-sm font-medium text-slate-700 mb-2">Tipo de servicio requerido *</label>
                    <Select value={formData.servicio} onValueChange={(value) => handleInputChange('servicio', value)}>
                      <SelectTrigger className="w-full border-slate-300 focus:border-amber-500 focus:ring-amber-500">
                        <SelectValue placeholder="Selecciona el servicio que necesitas" />
                      </SelectTrigger>
                      <SelectContent>
                        {services.map((service) => (
                          <SelectItem key={service.id} value={service.title}>
                            {service.title}
                          </SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                  </div>
                  
                  <div>
                    <label className="block text-sm font-medium text-slate-700 mb-2">Mensaje adicional</label>
                    <Textarea
                      value={formData.mensaje}
                      onChange={(e) => handleInputChange('mensaje', e.target.value)}
                      className="w-full h-32 border-slate-300 focus:border-amber-500 focus:ring-amber-500"
                      placeholder="Cuéntanos más detalles sobre tu proyecto o necesidades específicas..."
                    />
                  </div>
                  
                  <Button 
                    type="submit" 
                    disabled={isSubmitting}
                    className="w-full bg-gradient-to-r from-amber-500 to-orange-600 hover:from-amber-600 hover:to-orange-700 text-white font-semibold py-3 text-lg rounded-lg transition-all duration-300 shadow-lg hover:shadow-xl transform hover:scale-105 disabled:opacity-50 disabled:cursor-not-allowed"
                  >
                    {isSubmitting ? 'Enviando...' : 'Enviar solicitud'}
                  </Button>
                </form>
              </CardContent>
            </Card>
          </div>
        </div>
      </section>

      {/* Contact Section */}
      <section id="contacto" className="py-20 bg-slate-800">
        <div className="container mx-auto px-4">
          <div className="text-center mb-16">
            <h2 className="text-4xl md:text-5xl font-bold text-white mb-4">Contáctanos</h2>
            <p className="text-xl text-gray-300 max-w-3xl mx-auto">Estamos aquí para resolver todas tus dudas</p>
          </div>
          
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-8">
            <div className="text-center group">
              <div className="bg-gradient-to-r from-amber-500 to-orange-600 w-16 h-16 rounded-full flex items-center justify-center mx-auto mb-4 group-hover:scale-110 transition-transform duration-300">
                <MapPin className="text-white w-8 h-8" />
              </div>
              <h3 className="text-xl font-bold text-white mb-2">Dirección</h3>
              <p className="text-gray-300 text-sm">{companyInfo.contact.address}</p>
            </div>
            
            <div className="text-center group">
              <div className="bg-gradient-to-r from-amber-500 to-orange-600 w-16 h-16 rounded-full flex items-center justify-center mx-auto mb-4 group-hover:scale-110 transition-transform duration-300">
                <Phone className="text-white w-8 h-8" />
              </div>
              <h3 className="text-xl font-bold text-white mb-2">Teléfono</h3>
              <p className="text-gray-300 mb-2">{companyInfo.contact.phone}</p>
              <a
                href="https://wa.me/message/ITBOM5CJGNGQO1"
                target="_blank"
                rel="noopener noreferrer"
                className="inline-flex items-center space-x-2 bg-green-500 hover:bg-green-600 text-white px-4 py-2 rounded-lg text-sm transition-all duration-300 transform hover:scale-105"
              >
                <Phone className="w-4 h-4" />
                <span>WhatsApp</span>
              </a>
            </div>
            
            <div className="text-center group">
              <div className="bg-gradient-to-r from-amber-500 to-orange-600 w-16 h-16 rounded-full flex items-center justify-center mx-auto mb-4 group-hover:scale-110 transition-transform duration-300">
                <Mail className="text-white w-8 h-8" />
              </div>
              <h3 className="text-xl font-bold text-white mb-2">Email</h3>
              <p className="text-gray-300">{companyInfo.contact.email}</p>
            </div>
            
            <div className="text-center group">
              <div className="bg-gradient-to-r from-amber-500 to-orange-600 w-16 h-16 rounded-full flex items-center justify-center mx-auto mb-4 group-hover:scale-110 transition-transform duration-300">
                <Clock className="text-white w-8 h-8" />
              </div>
              <h3 className="text-xl font-bold text-white mb-2">Horario</h3>
              <p className="text-gray-300 text-sm">{companyInfo.contact.hours}</p>
            </div>
          </div>
        </div>
      </section>

      {/* WhatsApp Floating Button */}
      <a
        href="https://wa.me/message/ITBOM5CJGNGQO1"
        target="_blank"
        rel="noopener noreferrer"
        className="fixed bottom-6 right-6 z-50 bg-green-500 hover:bg-green-600 text-white p-4 rounded-full shadow-lg hover:shadow-xl transition-all duration-300 transform hover:scale-110 group"
        aria-label="Contactar por WhatsApp"
      >
        <Phone className="w-6 h-6" />
        <span className="absolute right-16 top-1/2 transform -translate-y-1/2 bg-gray-800 text-white px-3 py-1 rounded-lg text-sm whitespace-nowrap opacity-0 group-hover:opacity-100 transition-opacity duration-300 pointer-events-none">
          Contáctanos por WhatsApp
        </span>
      </a>

      {/* Footer */}
      <footer className="bg-slate-900 py-12">
        <div className="container mx-auto px-4">
          <div className="grid grid-cols-1 md:grid-cols-3 gap-8">
            <div>
              <div className="flex items-center space-x-4 mb-4">
                <img 
                  src="https://customer-assets.emergentagent.com/job_integral-supply/artifacts/tl2ojow0_WhatsApp%20Image%202025-10-06%20at%2016.54.25.jpeg" 
                  alt="GRUPO PINACSA Logo" 
                  className="h-12 w-auto object-contain bg-white p-2 rounded-lg"
                />
                <div>
                  <h3 className="text-xl font-bold text-white">{companyInfo.name}</h3>
                  <p className="text-gray-400 text-sm">Soluciones Integrales</p>
                </div>
              </div>
              <p className="text-gray-400 text-sm leading-relaxed">Todo lo que tu empresa necesita, en un solo proveedor. Suministros, mantenimiento y tecnología.</p>
            </div>
            
            <div>
              <h4 className="text-white font-bold mb-4">Enlaces Rápidos</h4>
              <ul className="space-y-2">
                <li><button onClick={() => scrollToSection('servicios')} className="text-gray-400 hover:text-amber-500 transition-colors duration-300">Servicios</button></li>
                <li><button onClick={() => scrollToSection('nosotros')} className="text-gray-400 hover:text-amber-500 transition-colors duration-300">Nosotros</button></li>
                <li><button onClick={() => scrollToSection('clientes')} className="text-gray-400 hover:text-amber-500 transition-colors duration-300">Clientes</button></li>
                <li><button onClick={() => scrollToSection('contacto')} className="text-gray-400 hover:text-amber-500 transition-colors duration-300">Contacto</button></li>
              </ul>
            </div>
            
            <div>
              <h4 className="text-white font-bold mb-4">Legal</h4>
              <ul className="space-y-2">
                <li><a href="#" className="text-gray-400 hover:text-amber-500 transition-colors duration-300">Aviso de Privacidad</a></li>
                <li><a href="#" className="text-gray-400 hover:text-amber-500 transition-colors duration-300">Términos y Condiciones</a></li>
              </ul>
            </div>
          </div>
          
          <div className="border-t border-gray-700 mt-8 pt-8 text-center">
            <p className="text-gray-400 text-sm">
              © 2025 {companyInfo.name}. Todos los derechos reservados.
            </p>
          </div>
        </div>
      </footer>
    </div>
  );
};

export default Landing;